import React, { useState, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Faq from "./Faq";
import homeslider from "../image/software_bg.jpg";
import gymsoft    from "../image/gym-software.jpg";
import renewal    from "../image/renewal.png";
import membership from "../image/software_membership.png";
import expense    from "../image/expense.png";
import crm        from "../image/crm.png";
import payment    from "../image/phonepe.png";
import pos        from "../image/pos.png";
import stock      from "../image/stock.png";
import search     from "../image/search.png";
import payroll    from "../image/payroll.jpg";
import customer   from "../image/customer.png";
import ss1 from "../image/ss1.png";
import ss2 from "../image/ss2.png";
import ss3 from "../image/ss3.png";
import ss4 from "../image/ss4.png";

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SoftwareApplication",
      "name": "MeraGym Gym Management Software",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web, Android, iOS",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR", "description": "Free trial available" },
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "512" },
      "description": "All-in-one gym management software to automate billing, track attendance, manage members, and grow your fitness business. Trusted by 5,000+ gyms across India.",
      "url": "https://meragym.com/gym-management-software",
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home",                    "item": "https://meragym.com" },
        { "@type": "ListItem", "position": 2, "name": "Gym Management Software", "item": "https://meragym.com/gym-management-software" },
      ],
    },
  ],
};

const FEATURES = [
  { img: renewal,    alt: "Renewal & Payment Management",    label: "Renewal & Payment Management" },
  { img: membership, alt: "Gym Membership Management",       label: "Membership Management" },
  { img: expense,    alt: "Expense Management",              label: "Expense & Expenditure Tracking" },
  { img: crm,        alt: "AI CRM for Leads",                label: "AI-Enabled CRM & Lead Management" },
  { img: payment,    alt: "Online Payment Collection",       label: "Online Payment Collection" },
  { img: pos,        alt: "POS for Store Sales",             label: "POS for Store & Supplement Sales" },
  { img: stock,      alt: "Inventory & Stock Management",    label: "Inventory & Stock Management" },
  { img: search,     alt: "Gym Listing on Search Portal",    label: "Listing on MeraGym Search Portal" },
  { img: payroll,    alt: "Salary & Payroll Management",     label: "Salary & Payroll Management" },
  { img: customer,   alt: "Customer & Tech Support",         label: "Dedicated Customer Support" },
];

const SCREENSHOTS = [
  { img: ss1, title: "Member Dashboard",     desc: "Complete overview of member activity and status" },
  { img: ss2, title: "Plan Management",      desc: "Create and manage unlimited membership plans" },
  { img: ss3, title: "Expense Management",   desc: "Track daily income, expenses, and profitability" },
  { img: ss4, title: "Attendance Tracking",  desc: "Biometric and app-based check-in monitoring" },
];

const WHY_CARDS = [
  { icon: "bi-lightning-charge-fill", colorClass: "gms-icon--amber",  title: "Quick Setup",            desc: "Go live in under 30 minutes — no technical expertise needed" },
  { icon: "bi-currency-rupee",        colorClass: "gms-icon--green",  title: "Cost-Effective",          desc: "Fraction of the cost of traditional gym software with zero hidden fees" },
  { icon: "bi-phone-fill",            colorClass: "gms-icon--indigo", title: "Mobile App Included",    desc: "Manage your gym on the go with our dedicated mobile application" },
  { icon: "bi-shield-fill-check",     colorClass: "gms-icon--sky",    title: "Secure & Reliable",      desc: "Bank-grade security with 99.9% uptime and daily data backups" },
  { icon: "bi-headset",               colorClass: "gms-icon--red",    title: "24/7 Support",            desc: "Dedicated support team via call, WhatsApp, and live chat" },
  { icon: "bi-graph-up-arrow",        colorClass: "gms-icon--violet", title: "Growth Analytics",       desc: "Data-driven insights to grow revenue and retain more members" },
];


export default function GymSoftware() {
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    axios
      .get(`${config.apiUrl}/page-video?page=gym_management_software`)
      .then((res) => { if (res.data.data) setVideos(res.data.data); })
      .catch(() => {});
  }, []);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Gym Management Software | Automate Your Gym — MeraGym</title>
          <meta
            name="description"
            content="MeraGym Gym Management Software helps you automate billing, track attendance, manage members, and grow your fitness business. Trusted by 5,000+ gyms across India. Start free today!"
          />
          <meta
            name="keywords"
            content="gym management software, gym management app, gym management system, gym software India, fitness center management software, gym billing software, gym membership management, gym attendance software, meragym software"
          />
          <link rel="canonical" href="https://meragym.com/gym-management-software" />
          <meta property="og:type"        content="website" />
          <meta property="og:title"       content="Gym Management Software | Automate Your Gym — MeraGym" />
          <meta property="og:description" content="All-in-one gym management software to automate billing, manage members, and grow your gym. Trusted by 5,000+ gyms across India." />
          <meta property="og:url"         content="https://meragym.com/gym-management-software" />
          <meta name="twitter:card"        content="summary_large_image" />
          <meta name="twitter:title"       content="Gym Management Software — MeraGym" />
          <meta name="twitter:description" content="Automate billing, attendance, and member management. Start your free gym software trial today." />
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      </HelmetProvider>

      {/* ── Hero ── */}
      <section className="gms-hero" aria-label="Gym Management Software">
        <img src={homeslider} alt="Gym Management Software by MeraGym" className="gms-hero-bg" />
        <div className="gms-hero-overlay"></div>
        <div className="container gms-hero-content">
          <nav className="gms-breadcrumb" aria-label="breadcrumb">
            <Link to="/">Home</Link>
            <i className="bi bi-chevron-right" aria-hidden="true"></i>
            <span aria-current="page">Gym Management Software</span>
          </nav>
          <span className="gms-hero-badge">
            <i className="bi bi-stars me-1" aria-hidden="true"></i> Trusted by 5,000+ Gyms Across India
          </span>
          <h1 className="gms-hero-heading">
            #1 Gym Management Software<br />
            <span className="gms-hero-highlight">Automate. Grow. Succeed.</span>
          </h1>
          <p className="gms-hero-sub">
            All-in-one gym management software to handle memberships, automate billing,
            track attendance, and boost member retention — built for Indian gym owners.
          </p>
          <div className="gms-hero-ctas">
            <a href="https://app.meragym.com" target="_blank" rel="noopener noreferrer" className="gms-btn-primary">
              Start Free Trial <i className="bi bi-arrow-right ms-2" aria-hidden="true"></i>
            </a>
            <a href="tel:+918920976591" className="gms-btn-secondary">
              <i className="bi bi-telephone-fill me-2" aria-hidden="true"></i> Call for Demo
            </a>
          </div>
          <div className="gms-hero-stats" aria-label="Platform statistics">
            <div className="gms-hero-stat"><strong>5,000+</strong><span>Gyms Onboarded</span></div>
            <div className="gms-hero-stat-div" aria-hidden="true"></div>
            <div className="gms-hero-stat"><strong>2 Lakh+</strong><span>Members Managed</span></div>
            <div className="gms-hero-stat-div" aria-hidden="true"></div>
            <div className="gms-hero-stat"><strong>₹0</strong><span>Setup Cost</span></div>
            <div className="gms-hero-stat-div" aria-hidden="true"></div>
            <div className="gms-hero-stat"><strong>24/7</strong><span>Support</span></div>
          </div>
        </div>
      </section>

      {/* ── What is MeraGym Software ── */}
      <section className="gms-intro-section" aria-labelledby="gms-intro-heading">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-5 text-center">
              <div className="gms-intro-img-wrap">
                <img src={gymsoft} className="img-fluid gms-intro-img" alt="MeraGym Gym Management Software dashboard" />
                <div className="gms-intro-badge">
                  <i className="bi bi-shield-fill-check me-1"></i> Verified by 5,000+ Gyms
                </div>
              </div>
            </div>
            <div className="col-lg-7">
              <span className="gms-section-pill">Why MeraGym</span>
              <h2 id="gms-intro-heading" className="gms-section-heading">
                The Best Gym Management Software for Indian Fitness Businesses
              </h2>
              <p className="gms-section-sub">
                MeraGym Gym Management Software is an all-in-one platform built specifically
                for Indian gym owners. Whether you run a small studio or a multi-branch chain,
                our software gives you every tool to automate operations, retain members, and
                grow revenue — at an unmatched price.
              </p>
              <ul className="gms-benefit-list">
                {[
                  "Cost-effective with zero hidden charges",
                  "Simple interface — no tech skills required",
                  "Complete automation: billing, renewals, reminders",
                  "Real-time data insights and revenue reports",
                  "Works on mobile, tablet, and desktop",
                  "Boost member engagement and reduce dropout",
                  "Secure, cloud-based, always up to date",
                ].map((item, i) => (
                  <li key={i}>
                    <i className="bi bi-check-circle-fill" aria-hidden="true"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="gms-cta-row">
                <a href="https://app.meragym.com" target="_blank" rel="noopener noreferrer" className="gms-btn-primary">
                  Start Free Trial <i className="bi bi-arrow-right ms-2"></i>
                </a>
                <a href="tel:+918920976591" className="gms-btn-outline">
                  <i className="bi bi-telephone-fill me-2"></i> Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="gms-features-section" aria-labelledby="gms-features-heading">
        <div className="container">
          <div className="gms-section-header">
            <span className="gms-section-pill">Features</span>
            <h2 id="gms-features-heading" className="gms-section-heading">
              Everything You Need in One Gym Management Software
            </h2>
            <p className="gms-section-sub">
              Powerful tools to run every aspect of your gym — from the front desk to the balance sheet
            </p>
          </div>
          <div className="gms-features-grid">
            {FEATURES.map((f, i) => (
              <div className="gms-feature-card" key={i}>
                <div className="gms-feature-img-wrap">
                  <img src={f.img} alt={f.alt} loading="lazy" />
                </div>
                <p>{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Screenshots ── */}
      <section className="gms-screenshots-section" aria-labelledby="gms-ss-heading">
        <div className="container">
          <div className="gms-section-header">
            <span className="gms-section-pill">Product Tour</span>
            <h2 id="gms-ss-heading" className="gms-section-heading">
              See Your Gym Management Software in Action
            </h2>
            <p className="gms-section-sub">
              A clean, intuitive interface designed for gym owners — not developers
            </p>
          </div>
          <div className="row g-4">
            {SCREENSHOTS.map((s, i) => (
              <div className="col-lg-6" key={i}>
                <div className="gms-ss-card">
                  <img src={s.img} alt={`${s.title} — MeraGym Gym Management Software`} className="img-fluid" loading="lazy" />
                  <div className="gms-ss-label">
                    <strong>{s.title}</strong>
                    <span>{s.desc}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── YouTube Videos (admin-controlled) ── */}
      {videos.length > 0 && (
        <section className="gms-video-section" aria-labelledby="gms-video-heading">
          <div className="container">
            <div className="gms-section-header">
              <span className="gms-section-pill">Watch &amp; Learn</span>
              <h2 id="gms-video-heading" className="gms-section-heading">
                See How MeraGym Gym Management Software Works
              </h2>
              <p className="gms-section-sub">Watch our demo videos to see the software in action</p>
            </div>
            <div className={`gms-video-grid${videos.length === 1 ? " gms-video-grid--single" : ""}`}>
              {videos.map((v, i) => (
                <div className="gms-video-item" key={i}>
                  <div className="gms-video-wrapper">
                    <iframe
                      src={v.embed_url}
                      title={v.title || `MeraGym Gym Management Software Demo ${i + 1}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    ></iframe>
                  </div>
                  {(v.title || v.description) && (
                    <div className="gms-video-meta">
                      {v.title && <strong>{v.title}</strong>}
                      {v.description && <span>{v.description}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Why Choose MeraGym ── */}
      <section className="gms-why-section" aria-labelledby="gms-why-heading">
        <div className="container">
          <div className="gms-section-header">
            <span className="gms-section-pill">Why MeraGym</span>
            <h2 id="gms-why-heading" className="gms-section-heading">
              Why 5,000+ Gym Owners Choose MeraGym Software
            </h2>
            <p className="gms-section-sub">
              Built for the unique challenges of running a gym in India
            </p>
          </div>
          <div className="gms-why-grid">
            {WHY_CARDS.map((c, i) => (
              <div className="gms-why-card" key={i}>
                <div className={`gms-why-icon ${c.colorClass}`}>
                  <i className={`bi ${c.icon}`}></i>
                </div>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ (dynamic from admin) ── */}
      <Faq page="gym_management_software" />

      {/* ── Final CTA ── */}
      <section className="gms-final-cta" aria-labelledby="gms-cta-heading">
        <div className="container">
          <div className="gms-final-cta-inner">
            <h2 id="gms-cta-heading">Ready to Transform Your Gym Management?</h2>
            <p>Join 5,000+ gym owners who automated their operations with MeraGym</p>
            <div className="gms-cta-row">
              <a href="https://app.meragym.com" target="_blank" rel="noopener noreferrer" className="gms-btn-primary">
                Start Free Trial <i className="bi bi-arrow-right ms-2"></i>
              </a>
              <a href="https://wa.me/+916390008506" target="_blank" rel="noreferrer" className="gms-btn-secondary">
                <i className="bi bi-whatsapp me-2"></i> Chat on WhatsApp
              </a>
            </div>
            <p className="gms-cta-note">No credit card required &nbsp;·&nbsp; Free setup &nbsp;·&nbsp; Cancel anytime</p>
          </div>
        </div>
      </section>
    </>
  );
}
